import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { useUserContext } from "../context/UserContext";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { useConversionJourney } from "../hooks/useConversionJourney";
import { track, trackGoogleAdsPurchaseConversion } from "../utils/analytics";
import { reportOnboardingStep } from "../services/LifecycleService";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import { AFTER_CHECKOUT_PATH } from "../utils/moreStores";
import { takeEmailDeal } from "../utils/emailDealReturn";

export default function SuccessPage() {
  useEnforceLightMode();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const journey = useConversionJourney("checkout_return");
  const { user, load } = useUserContext();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(10);
  const [isWaitingForWebhook, setIsWaitingForWebhook] = useState(true);
  const [pollAttempts, setPollAttempts] = useState(0);
  const trackedActivation = useRef(false);
  const trackedPurchaseConversion = useRef(false);
  const MAX_POLL_ATTEMPTS = 15;

  const hasRetailers = user?.retailers && user.retailers.length > 0;

  useEffect(() => {
    if (trackedPurchaseConversion.current) return;
    if (searchParams.get("retailer_added") === "true") return;
    trackedPurchaseConversion.current = true;
    const transactionId =
      searchParams.get("session_id") ??
      searchParams.get("payment_intent") ??
      "";
    trackGoogleAdsPurchaseConversion(transactionId);
  }, [searchParams]);

  useEffect(() => {
    if (hasRetailers && !trackedActivation.current) {
      trackedActivation.current = true;
      journey.once("entitled", "entitlement_available");
      track("subscription_activated");
      if (searchParams.get("retailer_added") !== "true") {
        // Every plan step shows the list layout now (see PaywallPage).
        track("paywall_variant_purchase", { variant: "list" });
      }
      reportOnboardingStep("completed");
      // Came from an email deal card: open that deal instead of onboarding.
      navigate(takeEmailDeal() ?? AFTER_CHECKOUT_PATH, { replace: true });
    }
  }, [hasRetailers, journey, navigate, searchParams]);

  // Poll for user data until retailers are set
  const pollUserData = useCallback(async () => {
    if (hasRetailers) {
      setIsWaitingForWebhook(false);
      return;
    }
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      journey.once("pending", "entitlement_pending");
      setIsWaitingForWebhook(false); // Give up and proceed anyway
      return;
    }
    setPollAttempts((prev) => prev + 1);
    await load();
  }, [hasRetailers, pollAttempts, load, journey]);

  // Poll effect
  useEffect(() => {
    if (!isWaitingForWebhook) return;
    const pollTimer = setInterval(pollUserData, 2000);
    pollUserData(); // Initial poll
    return () => clearInterval(pollTimer);
  }, [isWaitingForWebhook, pollUserData]);

  // Countdown effect - only starts after webhook processed or timeout
  useEffect(() => {
    if (isWaitingForWebhook) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = "/";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isWaitingForWebhook]);

  // Calculate progress for circular indicator (0 to 100)
  const progress = ((10 - countdown) / 10) * 100;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 dark:bg-dark-950 relative overflow-hidden">
      {/* Background Ambient Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-primary-200/30 dark:bg-primary-900/20 blur-3xl animate-pulse" />
        <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-purple-200/20 dark:bg-purple-900/10 blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="bg-white/80 dark:bg-dark-800/90 backdrop-blur-xl rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none border border-white/50 dark:border-dark-700 p-8 md:p-10 text-center transition-all duration-500 ease-out animate-fade-in">
          {/* Animated Success Icon */}
          <div className="relative mx-auto w-24 h-24 mb-8 flex items-center justify-center">
            {isWaitingForWebhook ? (
              <div className="relative w-20 h-20 bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center shadow-sm ring-4 ring-white dark:ring-dark-700">
                <Loader2 className="w-10 h-10 animate-spin" />
              </div>
            ) : (
              <>
                <div className="absolute inset-0 bg-green-50 dark:bg-green-900/30 rounded-full animate-ping opacity-75 duration-1000" />
                <div className="relative w-20 h-20 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center shadow-sm ring-4 ring-white dark:ring-dark-700">
                  <Check className="w-10 h-10 stroke-[3]" />
                </div>
              </>
            )}
          </div>

          {/* Main Content */}
          <div className="space-y-3 mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              {isWaitingForWebhook
                ? (t("success.activating") || "Activating Your Account...")
                : (t("success.paymentSuccessful") || "Payment Successful!")}
            </h1>
            <p className="text-gray-500 dark:text-dark-400 text-lg leading-relaxed">
              {isWaitingForWebhook
                ? (t("success.pleaseWait") || "Please wait while we set up your subscription...")
                : (t("success.thankYou") || "Thank you for your purchase. Your account has been upgraded.")}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-6">
            <button
              onClick={() => (window.location.href = "/")}
              disabled={isWaitingForWebhook}
              className={`group w-full flex items-center justify-center gap-2 font-semibold py-3.5 px-6 rounded-xl shadow-lg transition-all duration-200 ${
                isWaitingForWebhook
                  ? "bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-dark-400 cursor-not-allowed"
                  : "bg-primary-600 hover:bg-primary-700 text-white shadow-primary-600/20 hover:shadow-primary-600/30 hover:-translate-y-0.5 active:translate-y-0"
              }`}
            >
              <span>{isWaitingForWebhook ? "Please wait..." : "Go to Dashboard"}</span>
              {!isWaitingForWebhook && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
            </button>

            {/* Subtle Countdown or Processing Indicator */}
            <div className="flex items-center justify-center gap-3 text-sm text-gray-400 dark:text-dark-400 font-medium">
              {isWaitingForWebhook ? (
                <p className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("success.processing") || "Processing your payment..."}
                </p>
              ) : (
                <>
                  <div className="relative w-5 h-5">
                    <svg className="w-full h-full -rotate-90 transform" viewBox="0 0 36 36">
                      <path
                        className="text-gray-200 dark:text-dark-600"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="text-primary-500 dark:text-primary-400 transition-all duration-1000 ease-linear"
                        strokeDasharray="100, 100"
                        strokeDashoffset={100 - progress}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        pathLength="100"
                      />
                    </svg>
                  </div>
                  <p>
                    {t("success.redirecting") || "Redirecting in"}{" "}
                    <span className="tabular-nums text-gray-600 dark:text-dark-300">{countdown}s</span>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer Support Link */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 dark:text-dark-400">
            {t("success.supportMessage") || "Need help? Contact our "}{" "}
            <a
              href="#"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium hover:underline"
            >
              support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
